import axios from 'axios';

/**
 * True only for an HTTP 404 from the API.
 *
 * ── WHY THIS MATTERS FOR D-130 ─────────────────────────────────────────────
 * The customer location boundary is enforced entirely on the server. A record
 * that exists but is outside the caller's location scope is returned as a
 * uniform 404 — identical to a record that never existed — so the response can
 * never be used to probe for the existence of another location's data.
 *
 * The frontend's only job is to render that answer honestly. A 404 is a
 * PERMANENT outcome: retrying it can never succeed, and offering a "Try again"
 * button invites the customer to hammer an endpoint that will keep refusing.
 * It is also not an error in the sense the customer cares about — nothing is
 * broken, the record simply is not theirs to see.
 *
 * Deliberately narrow, mirroring `isForbiddenError`:
 *  - 401 is NOT not-found — session expiry belongs to the customer API client's
 *    interceptor, which clears the session and routes to login.
 *  - 403 is NOT not-found — that is `isForbiddenError`, a different message.
 *  - A network error, a timeout or a 5xx is NOT not-found — those are genuine
 *    failures and must keep their retryable error state.
 */
export function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}
